import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { useEffect } from 'react'

const changePasswordFormValidationSchema = z
  .object({
    password: z.string().min(1),
    passwordConfirmation: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.password && data.password.length > 0 && data.passwordConfirmation !== data.password) {
      ctx.addIssue({
        code: 'custom',
        message: 'formValidation.passwordConfirmation.isNotMatched',
        path: ['passwordConfirmation'],
      })
    }
  })

export type ChangePasswordFormValues = z.infer<typeof changePasswordFormValidationSchema>

type ChangePasswordDialogProps = {
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (values: ChangePasswordFormValues) => void
  open: boolean
}

// TODO: establish the contrainst for password to strengthen it
// like minimum characters, minuscule, majuscule, special characters, etc.
export default function ChangePasswordDialog(props: ChangePasswordDialogProps) {
  const { isSubmitting = false, onClose, onSubmit, open } = props
  const { t } = useTranslation()
  const isTooSmall = useIsMobile()

  const rhForm = useForm<ChangePasswordFormValues>({
    defaultValues: { password: '', passwordConfirmation: '' },
    resolver: zodResolver(changePasswordFormValidationSchema),
  })

  useEffect(() => {
    if (!open) {
      rhForm.reset()
    }
  }, [open, rhForm.reset])

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          minWidth: isTooSmall ? '350px' : '500px',
        },
      }}
    >
      <DialogTitle>Modify password</DialogTitle>
      <DialogContent>
        <form onSubmit={rhForm.handleSubmit(onSubmit)}>
          <Stack spacing={4}>
            <TextInputController control={rhForm.control} label={t('field.password.label', { context: 'new' })} name="password" type="password" />
            <TextInputController
              control={rhForm.control}
              label={t('field.passwordConfirmation.label', { context: 'new' })}
              name="passwordConfirmation"
              type="password"
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ pt: '8px' }}>
        <Button disableElevation onClick={onClose} variant="neutral">
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton color="primary" disableElevation loading={isSubmitting} onClick={rhForm.handleSubmit(onSubmit)} variant="contained">
          {t('common:button.submit.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
